import { useEffect, useMemo, useState } from 'react';

import { Edit, Check } from '@mui/icons-material';
import { Box, Stack, Typography, TextField, IconButton } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';

import ABTestLabelBox from 'domains/campaigns/components/ABTestLabelBox';
import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import {
    setHasRecommendedTime,
    setScheduledTimestamp,
    setSendName,
} from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { channelIcons } from 'domains/campaigns/pages/CurationsPage/utils';
import {
    ABTestType,
    CampaignChannelLabels,
    CampaignChannels,
    EmailSendInputState,
    SmsSendInputState,
    VoiceCallSendInput,
} from 'domains/campaigns/types';
import {
    getCampaignSendsForCriteriaBuilder,
    getIsRecurrenceContinuous,
    getIsRecurrenceEnabled,
    getSendDatetimeError,
    maxDate,
    maxTime,
    minDate,
    minTime,
} from 'domains/campaigns/utils';
import Alert from 'domains/core/components/Alert';
import DatePicker from 'domains/core/components/DatePicker';
import FormLayout from 'domains/core/components/FormLayout';
import InputLabel from 'domains/core/components/InputLabel';
import TimePicker from 'domains/core/components/TimePicker';
import useSegment from 'hooks/queries/useSegment';
import useTemplates from 'hooks/queries/useTemplates';
import useQueryParams from 'hooks/useQueryParams';
import { CampaignStatus } from 'models/enums';
import theme from 'theme';
import { getTimezone } from 'utils';

import DisabledTooltipContent from '../../../components/DisabledTooltipContent';
import { isReservedSendName } from '../utils';

import CampaignExitCriteria from './CampaignExitCriteria';
import ContactReEntrySchedule from './ContactReEntrySchedule';
import EndDateTimePicker from './EndDateTimePicker';
import RecommendedTimeSwitch from './RecommendedTimeSwitch';
import RecurrenceDropdown from './RecurrenceDropdown';
import { getRecommendedTime } from './utils/getRecommendedTime';

const smsSendTimeTooltip = 'SMS messages will be paused from 8 PM to 11 AM EST to comply with US telemarketing laws.';
const voiceCallSendTimeTooltip =
    'Voice calls will be paused from 9 PM to 11 AM EST to comply with US telemarketing laws.';
const cohortContinualError =
    'Cohorts cannot have continual sends. Please select a different recurrence or switch to an audience';
const getLabelPrefix = (isRecurrenceContinuous: boolean): string => (isRecurrenceContinuous ? 'Start' : 'Send');
const getDateLabel = (isRecurrenceContinuous: boolean): string => `${getLabelPrefix(isRecurrenceContinuous)} Date`;
const getTimeLabel = (isRecurrenceContinuous: boolean): string =>
    `${getLabelPrefix(isRecurrenceContinuous)} Time (${getTimezone()})`;

type Props = {
    campaignState: CampaignStateTypes;
    dispatchCampaignAction: React.Dispatch<any>;
    setActiveSendIndex?: (index: number) => void; // Optional: for managing active send index in A/B testing
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
    setNextStep: (step: CustomCampaignSteps) => void;
    setCustomContinueHandler?: (handler: (() => void) | null) => void; // Optional: custom continue handler
    targetSendIndex?: number; // Optional send index to target (defaults to 0)
    abTestVariant?: 'A' | 'B'; // For UI labeling in A/B testing
};

const CustomCampaignScheduleStep = ({
    campaignState,
    dispatchCampaignAction,
    setActiveSendIndex,
    setIsContinueEnabled,
    setNextStep,
    setCustomContinueHandler,
    targetSendIndex = 0, // Default to first send for backward compatibility
    abTestVariant,
}: Props) => {
    const query = useQueryParams();
    const status = query.get('status') as CampaignStatus | null;
    const disableEditing = status === CampaignStatus.PAUSED;
    const firstSendIndex = 0;
    const firstSend = campaignState.sends[firstSendIndex];
    const targetSend = campaignState.sends[targetSendIndex];
    const { channel, scheduledTimestamp } = targetSend;
    const isDirectMailSend = channel === CampaignChannels.DIRECT_MAIL;
    const isEmailSend = channel === CampaignChannels.EMAIL;
    const isSMSSend = channel === CampaignChannels.SMS;
    const isVoiceCallSend = channel === CampaignChannels.VOICE_CALL;

    // A/B Testing Content: Both Email-A and Email-B should have same schedule
    // A/B Testing Send Time: Each variant has different schedule times
    const isABTestingContent = campaignState.abTestConfig?.testOn === ABTestType.CONTENT;
    const isABTestingSendTime = campaignState.abTestConfig?.testOn === ABTestType.SEND_TIME;
    const sendCanRecur = isEmailSend || isSMSSend || isVoiceCallSend;
    const scheduledEndTimestamp =
        sendCanRecur && 'scheduledEndTimestamp' in firstSend ? firstSend.scheduledEndTimestamp : null;
    const recurrenceFrequency =
        sendCanRecur && 'recurrenceFrequency' in firstSend ? firstSend.recurrenceFrequency : null;
    const hasRecommendedTime =
        isEmailSend && 'hasRecommendedTime' in targetSend ? targetSend.hasRecommendedTime : false;
    const hasSplitType = isEmailSend && campaignState.sends.some((send) => 'splitType' in send);
    const { campaignExitCriteria: hasCampaignExitCriteriaFlag } = useFlags();
    // Global exit criteria only makes sense for multi-send campaigns (time-delay /
    // email-series / conditional-split) — a single send has no downstream send to gate.
    const shouldShowExitCriteria = hasCampaignExitCriteriaFlag && (campaignState.sends.length > 1 || hasSplitType);

    // Surface the campaign's own email sends in the exit-criteria engagement send pickers, including
    // ones that don't exist in the DB yet (mirroring conditional splits' own criteria builder).
    const exitCriteriaCampaignSends = useMemo(() => getCampaignSendsForCriteriaBuilder(campaignState.sends), [
        campaignState.sends,
    ]);

    const sendDateTimeLuxon = scheduledTimestamp ? DateTime.fromISO(scheduledTimestamp) : DateTime.now();
    const endDatetimeLuxon = scheduledEndTimestamp ? DateTime.fromISO(scheduledEndTimestamp) : null;

    const [sendDate, setSendDate] = useState<DateTime>(scheduledTimestamp ? sendDateTimeLuxon : null);
    const [sendTime, setSendTime] = useState<DateTime>(scheduledTimestamp ? sendDateTimeLuxon : null);
    const [hasEndDate, setHasEndDate] = useState(scheduledEndTimestamp ? true : false);
    const [endDate, setEndDate] = useState<DateTime>(endDatetimeLuxon);
    const [isRecommendedTimeSwitchTouched, setIsRecommendedTimeSwitchTouched] = useState(false);

    // Fetch templates for getting template name (used in A/B send time testing)
    const { data: templates = [] } = useTemplates();

    // State for editable send name (only for A/B testing)
    const [isEditingName, setIsEditingName] = useState(false);
    // Get the send name from campaignState
    const sendName = targetSend?.name || '';
    const curationName = campaignState.name || '';

    // Get template name for email sends
    const templateId =
        isEmailSend && 'templateId' in targetSend ? (targetSend as EmailSendInputState).templateId : null;
    const templateName = templateId ? templates.find((t) => t.id === templateId)?.name || '' : '';

    // Generate default name for A/B send time testing
    const generateDefaultSendTimeName = (sendIndex: number) => {
        const suffix = sendIndex === 0 ? 'Sendtime A' : 'Sendtime B';
        return `${curationName} - ${suffix}`;
    };

    // Get the display name: use sendName from campaignState if available, otherwise use default
    const getDisplayName = () => {
        if (sendName && sendName.trim() !== '') {
            return sendName;
        }
        // For A/B send time testing, use the specific naming convention
        if (isABTestingSendTime) {
            return generateDefaultSendTimeName(targetSendIndex);
        }
        // Return default with format: {curationName} - {templateName}.{uuid}
        return `${curationName} - ${templateName}.${uuidv4()}`;
    };
    const [customName, setCustomName] = useState<string>(getDisplayName());

    // Validation: Check if name is duplicate or reserved
    const getOtherSendName = () => {
        const otherIndex = targetSendIndex === 0 ? 1 : 0;
        return campaignState.sends[otherIndex]?.name || '';
    };
    const isDuplicateName = customName.trim() !== '' && customName === getOtherSendName();
    const isReservedName = isReservedSendName(customName);
    // Name validation only applies during A/B Send Time testing
    const hasNameError = isABTestingSendTime && (isDuplicateName || isReservedName);
    const nameErrorMessage = isDuplicateName
        ? 'Both send names must be different'
        : isReservedName
        ? 'Please choose a custom name for your send'
        : '';

    // Sync local state with campaignState when sendName changes
    useEffect(() => {
        setCustomName(getDisplayName());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sendName, targetSendIndex]);

    // Initialize send name in campaignState if it's empty (only for A/B send time testing)
    useEffect(() => {
        if (isABTestingSendTime && (!sendName || sendName.trim() === '')) {
            // Format: {curationName} - Sendtime A/B
            const defaultName = generateDefaultSendTimeName(targetSendIndex);
            dispatchCampaignAction(setSendName(defaultName, targetSendIndex));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isABTestingSendTime, targetSendIndex, curationName]);

    const isRecurrenceEnabled = getIsRecurrenceEnabled(recurrenceFrequency);
    const isRecurrenceContinuousForValidation = getIsRecurrenceContinuous(recurrenceFrequency);

    // Check if cohort is selected with continuous recurrence (invalid combination)
    const cohortId = sendCanRecur && 'cohortId' in firstSend ? firstSend.cohortId : null;
    const hasCohortContinualError = !!cohortId && isRecurrenceContinuousForValidation;

    // Scope the exit-criteria matching count to the campaign's starting (entry) audience. Only meaningful
    // for a plain-segment entry — hidden for cohort, A-B, or continual campaigns, where the starting
    // audience isn't a fixed segment. When the entry definition isn't already in builder state (a saved
    // audience stores only its id), fetch it so the count can still be scoped.
    const isAbTestCampaign = campaignState.abTestConfig != null;
    const isContinualCampaign = isRecurrenceContinuousForValidation || campaignState.reentryDetails != null;
    const canScopeExitCount = shouldShowExitCriteria && !cohortId && !isAbTestCampaign && !isContinualCampaign;
    const entrySegmentId = 'segmentId' in firstSend ? firstSend.segmentId : undefined;
    const inlineEntryDefinition = 'segmentDefinition' in firstSend ? firstSend.segmentDefinition : undefined;
    const { data: entrySegment } = useSegment(entrySegmentId, {
        enabled: canScopeExitCount && !inlineEntryDefinition && Boolean(entrySegmentId),
    });
    const exitCriteriaStartingDefinition = canScopeExitCount
        ? inlineEntryDefinition ?? entrySegment?.segmentDefinition ?? null
        : null;

    // Get error messages for date/time validation
    // Skip past date error when editing a paused curation
    const getFilteredDatetimeError = (datetime: DateTime, isDate: boolean) => {
        const error = getSendDatetimeError(datetime, isDate);
        // Skip past date error when editing a paused curation
        if (disableEditing && error === 'A Curation cannot be scheduled in the past.') {
            return undefined;
        }
        return error;
    };

    const sendDateErrorMessage = sendDate && getFilteredDatetimeError(sendDate, true);
    const sendTimeErrorMessage = sendTime && getFilteredDatetimeError(sendTime, false);

    useEffect(() => {
        // For send time A/B testing, handle flow: Email-A Schedule → Email-B Schedule → Publish
        if (isABTestingSendTime && abTestVariant && setActiveSendIndex) {
            if (abTestVariant === 'A' && setCustomContinueHandler) {
                // Email-A: Set custom continue handler to switch to Email-B
                const customHandler = () => {
                    setActiveSendIndex(1); // Switch to Email-B
                };

                setCustomContinueHandler(customHandler);
                return; // Don't set next step, custom handler will manage navigation
            } else if (abTestVariant === 'B') {
                // Email-B: Clear custom handler and set normal next step
                if (setCustomContinueHandler) {
                    setCustomContinueHandler(null);
                }
                setNextStep(CustomCampaignSteps.PUBLISH_CAMPAIGN);
            }
        } else {
            // Normal flow or content A/B testing: Clear custom handler and set normal next step
            if (setCustomContinueHandler) {
                setCustomContinueHandler(null);
            }
            setNextStep(CustomCampaignSteps.PUBLISH_CAMPAIGN);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [abTestVariant, isABTestingSendTime]);

    useEffect(() => {
        setIsContinueEnabled(
            !!sendDate &&
                !!sendTime &&
                !sendDateErrorMessage &&
                !sendTimeErrorMessage &&
                !hasNameError &&
                !hasCohortContinualError
        );
    }, [
        sendDate,
        sendDateErrorMessage,
        sendTime,
        sendTimeErrorMessage,
        setIsContinueEnabled,
        hasNameError,
        hasCohortContinualError,
    ]);

    const handleChangeSendDateTime = (dateTime: DateTime, isDate?: boolean) => {
        if (isEmailSend) {
            setIsRecommendedTimeSwitchTouched(true);
        }

        isDate ? setSendDate(dateTime) : setSendTime(dateTime);

        if (!dateTime?.isValid && isEmailSend) {
            dispatchCampaignAction(setHasRecommendedTime(false, targetSendIndex));
            return;
        }

        // Updating sendDateTimeLuxon regardless of whether it's date or time
        const updatedSendDateTime = sendDateTimeLuxon.set({
            year: isDate ? dateTime.year : sendDateTimeLuxon.year,
            month: isDate ? dateTime.month : sendDateTimeLuxon.month,
            day: isDate ? dateTime.day : sendDateTimeLuxon.day,
            hour: !isDate ? dateTime.hour : sendDateTimeLuxon.hour,
            minute: !isDate ? dateTime.minute : sendDateTimeLuxon.minute,
        });

        // Recommended time should be considered only when changing date and having recommended time enabled
        const shouldHaveRecommendedTime = isDate && hasRecommendedTime;

        // Get recommended time if necessary
        const finalDateTime = shouldHaveRecommendedTime
            ? getRecommendedTime(updatedSendDateTime, recurrenceFrequency)
            : updatedSendDateTime;

        // A/B Testing Content: Update both Email-A and Email-B with same schedule
        // A/B Testing Send Time: Update only the current send (since they test different times)
        if (isABTestingContent) {
            dispatchCampaignAction(setScheduledTimestamp(finalDateTime.toISO(), 0)); // Email-A
            dispatchCampaignAction(setScheduledTimestamp(finalDateTime.toISO(), 1)); // Email-B
        } else {
            dispatchCampaignAction(setScheduledTimestamp(finalDateTime.toISO(), targetSendIndex));
        }

        if (isEmailSend) {
            dispatchCampaignAction(setHasRecommendedTime(shouldHaveRecommendedTime, targetSendIndex));
        }

        if (!getFilteredDatetimeError(finalDateTime, isDate)) {
            setSendDate(finalDateTime);
            setSendTime(finalDateTime);
        }
    };

    const handleRecommendedTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsRecommendedTimeSwitchTouched(true);

        if (e.target.checked) {
            const newDatetime = getRecommendedTime(DateTime.fromISO(scheduledTimestamp), recurrenceFrequency);

            setSendTime(newDatetime);

            // A/B Testing Content: Update both Email-A and Email-B with same schedule
            if (isABTestingContent) {
                dispatchCampaignAction(setScheduledTimestamp(newDatetime.toISO(), 0)); // Email-A
                dispatchCampaignAction(setScheduledTimestamp(newDatetime.toISO(), 1)); // Email-B
            } else {
                dispatchCampaignAction(setScheduledTimestamp(newDatetime.toISO(), targetSendIndex));
            }

            dispatchCampaignAction(setHasRecommendedTime(true, targetSendIndex));
        } else {
            dispatchCampaignAction(setHasRecommendedTime(false, targetSendIndex));
        }
    };

    const shouldShowSendTimeAlert = isDirectMailSend && scheduledTimestamp;
    const shouldShowRecommendedTimeSwitch = isEmailSend;
    const shouldShowReccurenceDropdown = sendCanRecur && sendDate && !campaignState.abTestConfig;
    const shouldShowEndDateTimePicker = sendDate && isRecurrenceEnabled;
    const isRecurrenceContinuous = getIsRecurrenceContinuous(recurrenceFrequency);
    const shouldShowContactReEntrySchedule = isRecurrenceEnabled && !hasSplitType && !isVoiceCallSend;

    const StepIcon = channelIcons[channel];

    const getSlotProps = (isTime?: boolean) => ({
        textField: {
            error: isTime ? !!sendTimeErrorMessage : !!sendDateErrorMessage,
            helperText: isTime ? sendTimeErrorMessage : sendDateErrorMessage,
            sx: { width: '100%' },
        },
    });

    const getSendTimeTooltip = (channel: CampaignChannels) => {
        if (channel === CampaignChannels.SMS) {
            return smsSendTimeTooltip;
        }
        if (channel === CampaignChannels.VOICE_CALL) {
            return voiceCallSendTimeTooltip;
        }
        return '';
    };

    // Handlers for editing send name (A/B testing only)
    const handleEditClick = () => {
        setIsEditingName(true);
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCustomName(event.target.value);
    };

    const handleNameSave = () => {
        // Don't save if name is duplicate or reserved
        if (hasNameError) {
            return;
        }

        setIsEditingName(false);
        // Dispatch action to save customName to campaignState
        dispatchCampaignAction(setSendName(customName, targetSendIndex));
    };

    const handleNameKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleNameSave();
        }
    };

    const getBadgeContent = () => {
        if (isABTestingSendTime && abTestVariant === 'A') {
            return { text: 'Send Time A', badgeLabel: 'A' };
        }
        if (isABTestingSendTime && abTestVariant === 'B') {
            return { text: 'Send Time B', badgeLabel: 'B' };
        }
        return null;
    };

    const badgeContent = getBadgeContent();

    return (
        <FormLayout
            header={'schedule content'}
            stepName={CampaignChannelLabels[channel]}
            icon={<StepIcon />}
            guidingText="Set the date and time of your send. Have your audience in mind to optimize your send time."
        >
            <Stack rowGap={4}>
                {badgeContent && (
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <ABTestLabelBox label={badgeContent.badgeLabel} size="medium" />

                        {/* Only show editable name for A/B Send Time Testing */}
                        {isABTestingSendTime ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                                {isEditingName ? (
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <TextField
                                                value={customName}
                                                onChange={handleNameChange}
                                                onKeyPress={handleNameKeyPress}
                                                onBlur={handleNameSave}
                                                autoFocus
                                                variant="standard"
                                                error={hasNameError}
                                                helperText={nameErrorMessage}
                                                sx={{
                                                    width: '100%',
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '1.25rem',
                                                        fontWeight: 'bold',
                                                    },
                                                }}
                                            />
                                        </Box>
                                        <IconButton
                                            size="small"
                                            onClick={handleNameSave}
                                            disabled={hasNameError}
                                            sx={{ padding: 0.5, mt: 0.5 }}
                                        >
                                            <Check fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="h6" fontWeight="bold">
                                                {customName}
                                            </Typography>
                                            <IconButton size="small" onClick={handleEditClick} sx={{ padding: 0.5 }}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        {hasNameError && (
                                            <Typography variant="caption" color="error" fontSize={12}>
                                                {nameErrorMessage}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            <Typography variant="h6" fontWeight="bold">
                                {badgeContent.text}
                            </Typography>
                        )}
                    </Stack>
                )}
                <DisabledTooltipContent disabled={disableEditing}>
                    <span style={{ width: '100%' }}>
                        <DatePicker
                            data-testid="date-picker"
                            label={getDateLabel(isRecurrenceContinuous)}
                            disabled={disableEditing}
                            value={sendDate}
                            minDate={minDate()}
                            maxDate={maxDate()}
                            onChange={(dateTime) => handleChangeSendDateTime(dateTime, true)}
                            slotProps={{
                                ...getSlotProps(),
                                popper: {
                                    style: { paddingRight: theme.spacing(3.25) },
                                },
                            }}
                        />
                    </span>
                </DisabledTooltipContent>
                <DisabledTooltipContent disabled={disableEditing}>
                    <span style={{ width: '100%' }}>
                        <TimePicker
                            label={
                                <InputLabel
                                    label={getTimeLabel(isRecurrenceContinuous)}
                                    labelIconTooltipText={getSendTimeTooltip(channel)}
                                />
                            }
                            disabled={disableEditing}
                            value={sendTime}
                            minTime={minTime(scheduledTimestamp)}
                            maxTime={maxTime(scheduledTimestamp)}
                            onChange={(dateTime) => handleChangeSendDateTime(dateTime)}
                            slotProps={getSlotProps(true)}
                            sx={{ pb: 2 }}
                        />
                    </span>
                </DisabledTooltipContent>
                {shouldShowSendTimeAlert && (
                    <Alert
                        elevation={0}
                        severity="info"
                        variant="outlined"
                        style={{ borderColor: theme.palette.info.main, borderWidth: '2px' }}
                    >
                        Files will start printing the business day after the schedule date
                    </Alert>
                )}
                {shouldShowRecommendedTimeSwitch && (
                    <Box my={-1} pl={1}>
                        <RecommendedTimeSwitch
                            isChecked={hasRecommendedTime}
                            isDisabled={!!sendDateErrorMessage}
                            isTouched={isRecommendedTimeSwitchTouched}
                            recurrenceFrequency={recurrenceFrequency}
                            sendDate={sendDate}
                            onChange={handleRecommendedTimeChange}
                        />
                    </Box>
                )}
                {shouldShowReccurenceDropdown && sendCanRecur && (
                    <RecurrenceDropdown
                        disableButton={disableEditing}
                        dispatchCampaignAction={dispatchCampaignAction}
                        firstSend={firstSend as EmailSendInputState | SmsSendInputState | VoiceCallSendInput}
                        sendDate={sendDate}
                        setEndDate={setEndDate}
                        setHasEndDate={setHasEndDate}
                        setIsRecommendedTimeSwitchTouched={setIsRecommendedTimeSwitchTouched}
                        setSendTime={setSendTime}
                    />
                )}
                {hasCohortContinualError && (
                    <Alert
                        data-testid="cohort-continual-error"
                        elevation={0}
                        severity="error"
                        variant="outlined"
                        style={{ borderColor: theme.palette.error.main, borderWidth: '2px' }}
                    >
                        {cohortContinualError}
                    </Alert>
                )}
                {shouldShowEndDateTimePicker && (
                    <EndDateTimePicker
                        dispatchCampaignAction={dispatchCampaignAction}
                        endDate={endDate}
                        hasEndDate={hasEndDate}
                        sendDate={sendDate}
                        setEndDate={setEndDate}
                        setHasEndDate={setHasEndDate}
                        setIsContinueEnabled={setIsContinueEnabled}
                        setIsRecommendedTimeSwitchTouched={setIsRecommendedTimeSwitchTouched}
                        disabled={disableEditing}
                    />
                )}
                {shouldShowContactReEntrySchedule && (
                    <ContactReEntrySchedule
                        dispatchCampaignAction={dispatchCampaignAction}
                        reentryDetails={campaignState?.reentryDetails}
                        setIsContinueEnabled={setIsContinueEnabled}
                        disabled={disableEditing}
                    />
                )}
                {shouldShowExitCriteria && (
                    <CampaignExitCriteria
                        dispatchCampaignAction={dispatchCampaignAction}
                        exitCriteria={campaignState?.exitCriteria}
                        setIsContinueEnabled={setIsContinueEnabled}
                        campaignSends={exitCriteriaCampaignSends}
                        startingAudienceDefinition={exitCriteriaStartingDefinition}
                    />
                )}
            </Stack>
        </FormLayout>
    );
};

export default CustomCampaignScheduleStep;
